import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Button, Description, Heading, Separator, Spinner } from '@heroui/react';
import { useAuth } from '../../../hooks/useAuth';
import { doSignOut, refreshUser, resendVerification } from '../../../lib/firebase';
import { showErrorFromUnknown } from '../../../lib/toast';
import { AsyncButton } from '../../../components/ui/async-button';
import { StatusCallout } from '../../../components/ui/status-callout';
import { AuthSplitLayout } from '../components/auth-split-layout';
import { getPostAuthPath } from '../utils/post-auth-redirect';

type FeedbackType = 'success' | 'warning' | 'danger';

export default function VerifyEmailPage() {
  const { authUser, userDoc, loading, setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [feedback, setFeedback] = useState<{ type: FeedbackType; message: string } | null>(null);

  const redirectAfterVerify = useCallback(() => {
    navigate(getPostAuthPath(userDoc), { replace: true });
  }, [navigate, userDoc]);

  useEffect(() => {
    const oobCode = searchParams.get('oobCode');
    const mode = searchParams.get('mode');
    if (oobCode && mode) {
      navigate(`/email-action?${searchParams.toString()}`, { replace: true });
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      setFeedback({ type: 'danger', message: decodeURIComponent(error) });
    }
  }, [searchParams]);

  useEffect(() => {
    if (loading || !authUser?.emailVerified) return;
    redirectAfterVerify();
  }, [authUser?.emailVerified, loading, redirectAfterVerify]);

  const handleResend = async () => {
    if (!authUser) return;
    setResending(true);
    setFeedback(null);
    try {
      await resendVerification(authUser);
      setFeedback({
        type: 'success',
        message: 'Email de verificação reenviado! Verifique sua caixa de entrada.',
      });
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao reenviar');
      setFeedback({ type: 'danger', message: 'Erro ao reenviar email. Tente novamente.' });
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!authUser) return;
    setChecking(true);
    setFeedback(null);
    try {
      const refreshed = await refreshUser(authUser);
      setAuthUser(refreshed);

      if (refreshed.emailVerified) {
        setFeedback({ type: 'success', message: 'Email verificado com sucesso! Redirecionando...' });
        setTimeout(redirectAfterVerify, 800);
      } else {
        setFeedback({
          type: 'warning',
          message:
            'Email ainda não verificado. Clique no link enviado para seu email antes de continuar.',
        });
      }
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao verificar');
      setFeedback({ type: 'danger', message: 'Erro ao verificar o status do email. Tente novamente.' });
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      await doSignOut();
      navigate('/login', { replace: true });
    } catch (err) {
      showErrorFromUnknown(err, 'Erro ao sair');
    }
  };

  if (loading) {
    return (
      <AuthSplitLayout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </AuthSplitLayout>
    );
  }

  if (!authUser) {
    return (
      <AuthSplitLayout>
        <div className="w-full max-w-sm space-y-6">
          <div className="flex w-full flex-col items-center space-y-1 text-center lg:items-start lg:text-left">
            <Heading level={2} className="w-full text-center text-2xl font-semibold lg:text-left">
              Verificação de email
            </Heading>
            <Description className="text-center lg:text-left">
              {feedback?.message ?? 'Link de verificação inválido ou expirado.'}
            </Description>
          </div>
          <Button variant="primary" size="lg" fullWidth onPress={() => navigate('/login')}>
            Ir para o login
          </Button>
        </div>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout>
      <div className="w-full max-w-sm space-y-6">
        <div className="flex w-full flex-col items-center space-y-2 text-center lg:items-start lg:text-left">
          <Heading level={2} className="w-full text-center text-2xl font-semibold lg:text-left">
            Verifique seu email
          </Heading>
          <Description className="text-pretty text-center lg:text-left">
            Enviamos um link de verificação para
          </Description>
          <p className="break-all text-center font-medium text-foreground lg:text-left">
            {authUser.email}
          </p>
          <Description className="text-pretty text-center lg:text-left">
            Abra o email e clique no link para continuar.
          </Description>
        </div>

        {feedback ? (
          <StatusCallout status={feedback.type}>{feedback.message}</StatusCallout>
        ) : null}

        <div className="flex flex-col gap-5">
          <AsyncButton
            variant="primary"
            size="lg"
            fullWidth
            isPending={resending}
            isDisabled={checking}
            onPress={handleResend}
          >
            Reenviar email
          </AsyncButton>

          <AsyncButton
            variant="secondary"
            size="lg"
            fullWidth
            isPending={checking}
            isDisabled={resending}
            onPress={handleCheckVerification}
          >
            Já verifiquei meu email
          </AsyncButton>

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-muted text-xs uppercase tracking-wide">ou</span>
            <Separator className="flex-1" />
          </div>

          <Button variant="ghost" size="lg" fullWidth onPress={handleLogout}>
            <LogOut size={18} />
            Usar outra conta
          </Button>
        </div>

        <p className="text-pretty text-center text-xs text-muted lg:text-left">
          Não recebeu? Confira spam ou lixo eletrônico.
        </p>
      </div>
    </AuthSplitLayout>
  );
}
